import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Typography,
  Alert,
  Paper,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Close,
  Contacts as ContactsIcon,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import useFetchDataWithPagination from "../../../utility/useFetchDataWithPagination";
import usePostData from "../../../utility/usePostData";

const ContactLinkManagement = () => {
  const { isLoading, error, submitData } = usePostData();
  const { data, isLoading: loading, paginate, refreshSearch } =
    useFetchDataWithPagination("contact-links");

  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [contactLinks, setContactLinks] = useState([]);

  const [formData, setFormData] = useState({
    name_en: "",
    name_fr: "",
    description_en: "",
    description_fr: "",
    email: "",
    phone: "",
    address_en: "",
    address_fr: "",
    box: "",
  });

  useEffect(() => {
    if (data?.data?.data) {
      setContactLinks(data.data.data);
    }
  }, [data]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditMode(true);
      setCurrentItem(item);
      setFormData(item);
    } else {
      setEditMode(false);
      setFormData({
        name_en: "",
        name_fr: "",
        description_en: "",
        description_fr: "",
        email: "",
        phone: "",
        address_en: "",
        address_fr: "",
        box: "",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setErrorMessage("");
    setCurrentItem(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name_en && !formData.name_fr) {
      setErrorMessage("Veuillez saisir au moins un nom (EN ou FR).");
      return;
    }

    const endpoint = editMode
      ? `contact-links/${currentItem.id}`
      : "contact-links";
    const method = editMode ? "PUT" : "POST";

    submitData(endpoint, formData, method);
    refreshSearch();
    handleCloseDialog();
  };

  const handleDelete = (id) => {
    if (window.confirm("Supprimer ce contact ?")) {
      submitData(`contact-links/${id}`, null, "DELETE");
      refreshSearch();
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {isLoading && <LinearProgress />}

      {/* HEADER */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 4 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <ContactsIcon color="primary" sx={{ fontSize: 40 }} />
          <Typography variant="h4" fontWeight={700} color="primary">
            Contacts
          </Typography>
        </Stack>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Nouveau contact
        </Button>
      </Stack>

      {error && <Alert severity="error">{error?.message}</Alert>}
      {loading && <LinearProgress color="secondary" sx={{ mb: 2 }} />}

      {/* LISTE */}
      <Grid container spacing={3}>
        {Array.isArray(contactLinks) && contactLinks.length > 0 ? (
          contactLinks.map((item, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": { transform: "translateY(-4px)", boxShadow: 6 },
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" fontWeight={600}>
                    {item.name_en || item.name_fr}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {item.description_en || item.description_fr}
                  </Typography>

                  <Stack spacing={0.5}>
                    {item.email && (
                      <Typography variant="caption">📧 {item.email}</Typography>
                    )}
                    {item.phone && (
                      <Typography variant="caption">📞 {item.phone}</Typography>
                    )}
                    {item.address_en && (
                      <Typography variant="caption">
                        📍 {item.address_en}
                      </Typography>
                    )}
                    {item.box && (
                      <Typography variant="caption">📦 {item.box}</Typography>
                    )}
                  </Stack>
                </CardContent>

                <Box sx={{ p: 2, pt: 0 }}>
                  <Stack direction="row" justifyContent="flex-end" spacing={1}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenDialog(item)}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(item.id)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
              </Card>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                textAlign: "center",
                py: 8,
                bgcolor: "grey.50",
                borderRadius: 2,
              }}
            >
              <ContactsIcon sx={{ fontSize: 80, color: "grey.300", mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Aucun contact disponible
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenDialog()}
                sx={{ mt: 2 }}
              >
                Ajouter le premier contact
              </Button>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* PAGINATION */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        {paginate()}
      </Box>

      {/* FORMULAIRE DIALOG */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth>
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">
              {editMode ? "Modifier le contact" : "Nouveau contact"}
            </Typography>
            <IconButton onClick={handleCloseDialog}>
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}
          <Stack spacing={2}>
            <TextField
              label="Nom (EN)"
              name="name_en"
              value={formData.name_en}
              onChange={handleInputChange}
            />
            <TextField
              label="Nom (FR)"
              name="name_fr"
              value={formData.name_fr}
              onChange={handleInputChange}
            />
            <TextField
              label="Description (EN)"
              name="description_en"
              value={formData.description_en}
              onChange={handleInputChange}
            />
            <TextField
              label="Description (FR)"
              name="description_fr"
              value={formData.description_fr}
              onChange={handleInputChange}
            />
            <TextField
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
            />
            <TextField
              label="Téléphone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
            />
            <TextField
              label="Adresse (EN)"
              name="address_en"
              value={formData.address_en}
              onChange={handleInputChange}
            />
            <TextField
              label="Adresse (FR)"
              name="address_fr"
              value={formData.address_fr}
              onChange={handleInputChange}
            />
            <TextField
              label="Boîte postale"
              name="box"
              value={formData.box}
              onChange={handleInputChange}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={isLoading}>
            {editMode ? "Mettre à jour" : "Créer"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ContactLinkManagement;
